import { format } from 'date-fns';
import React from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { Reddit, Twitter } from '@mui/icons-material';
import { SvgIcon } from '@mui/material';

import Chip from 'app/ui-kit/components/Chip';
import IconEmail from 'app/ui-kit/icons/EyeOpen.svg';
import IconVirus from 'app/ui-kit/icons/Virus.svg';
import { emptyArray } from 'main/components/SearchBar/constants';
import isEmpty from 'utils/is-empty';
import Utils from 'utils/Utils';

const useStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 24,
        '@media (max-width: 960px)': {
            marginBottom: 0
        }
    },
    line: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    },
    date: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap'
    },
    time: {
        color: '#aaa'
    },
    tagIcon: {
        width: 16,
        height: 16
    }
});

interface Props {
    data: {
        id: string;
        type: string;
        published: string;
        cwe?: string[];
        reporter?: string;
        viewCount: number;
        href?: string;
        bounty?: number;
        enchantments: {
            twitter?: {
                counter: number;
            };
            reddit?: {
                counter: number;
            };
            exploitation?: {
                wildExploited: boolean;
                wildExploitedSources: {
                    idList: string[];
                    type: string;
                }[];
            };
            tags?: string[];
        };
    };
    className?: string;
}

const Tags: React.FC<Props> = ({
    data: {
        id,
        type,
        published,
        cwe = emptyArray,
        reporter,
        viewCount,
        href,
        bounty,
        enchantments: { twitter, reddit, exploitation, tags }
    },
    className
}) => {
    const { classes, cx } = useStyles();
    const history = useHistory();

    return (
        <div className={cx(classes.container, className)}>
            <div className={cx(classes.line, className)}>
                <Chip
                    label={
                        <span className={classes.date}>
                            {format(new Date(published), 'yyyy-MM-dd')}
                            <span className={classes.time}>{format(new Date(published), 'HH:mm:ss')}</span>
                        </span>
                    }
                />

                {cwe.filter(Boolean).map((cwe) => (
                    <Chip
                        key={cwe}
                        label={cwe}
                        onClick={() => Utils.openUrl(`https://cwe.mitre.org/data/definitions/${id}.html`)}
                    />
                ))}

                {reporter ? (
                    reporter.toLowerCase() === type ? null : (
                        <Chip
                            tooltip={'Reporter'}
                            label={Utils.isMobile() ? Utils.trunc(32, reporter) : reporter}
                            onClick={() => history.push(`/search?query=reporter:"${reporter}"`)}
                        />
                    )
                ) : null}

                {href ? (
                    <Chip
                        tooltip={'Source'}
                        label={href.startsWith('http') ? new URL(href).host : 'link'}
                        onClick={() => Utils.openUrl(href)}
                    />
                ) : null}

                {bounty ? <Chip tooltip="Bounty Amount" label={'$' + bounty} /> : null}

                {viewCount ? (
                    <Chip
                        tooltip="View Count"
                        label={viewCount}
                        icon={<SvgIcon className={classes.tagIcon} component={IconEmail} />}
                    />
                ) : null}

                {exploitation?.wildExploited ? (
                    <Chip
                        tooltip="Exploited in the wild"
                        label="In Wild"
                        icon={<SvgIcon className={classes.tagIcon} component={IconVirus} />}
                        onClick={() =>
                            history.push(
                                '/search?query='.concat(
                                    exploitation.wildExploitedSources
                                        .map(({ idList }) => idList.map((id) => `"${id}"`))
                                        .flat()
                                        .join(' OR ')
                                )
                            )
                        }
                    />
                ) : null}

                {twitter?.counter ? (
                    <Chip label={twitter.counter} icon={<Twitter className={classes.tagIcon} />} />
                ) : null}

                {reddit?.counter ? <Chip label={reddit.counter} icon={<Reddit className={classes.tagIcon} />} /> : null}
            </div>
            {isEmpty(tags) ? null : (
                <div className={classes.line}>
                    {tags?.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            onClick={() => history.push(`/search?query=enchantments.tags:"${encodeURIComponent(tag)}"`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Tags;
